import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../api';

function MasrafiRequests() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [confirmingId, setConfirmingId] = useState('');
  const [rejectingId, setRejectingId] = useState('');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiFetch('/masrafi-requests');
      const data = response ? await response.json().catch(() => ({})) : {};
      if (!response?.ok) {
        setError(data.message || 'فشل جلب طلبات مصرفي');
        return;
      }
      setRequests(data.requests || []);
    } catch (err) {
      console.error(err);
      setError('حدث خطأ أثناء جلب طلبات مصرفي');
    } finally {
      setLoading(false);
    }
  };

  const normalizeArabic = (value) => String(value || '')
    .toLowerCase()
    .replace(/[أإآا]/g, 'ا')
    .replace(/[ى]/g, 'ي')
    .replace(/[ؤ]/g, 'و')
    .replace(/[ئ]/g, 'ي')
    .replace(/[ة]/g, 'ه')
    .replace(/[٠-٩]/g, (digit) => '٠١٢٣٤٥٦٧٨٩'.indexOf(digit))
    .replace(/\s+/g, ' ')
    .trim();

  const formatNumber = (value) => {
    const number = Number(value || 0);
    if (!Number.isFinite(number)) return '0';
    return number.toLocaleString('en-US', { maximumFractionDigits: 2 });
  };

  const formatDate = (value) => {
    if (!value) return '-';
    try {
      return new Date(value).toLocaleString('en-GB', {
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'بانتظار تأكيد الشركة',
      completed: 'مؤكد ومكتمل',
      rejected: 'مرفوض',
      cancelled: 'ملغي'
    };
    return labels[status] || status || '-';
  };

  const getStatusClass = (status) => {
    const classes = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      completed: 'bg-green-100 text-green-800 border-green-200',
      rejected: 'bg-red-100 text-red-800 border-red-200',
      cancelled: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return classes[status] || classes.pending;
  };

  const filteredRequests = useMemo(() => {
    const query = normalizeArabic(searchText);
    return requests
      .filter((item) => statusFilter === 'all' ? true : item.status === statusFilter)
      .filter((item) => {
        if (!query) return true;
        const text = normalizeArabic([
          item.request_code,
          item.store_name,
          item.user_name,
          item.amount,
          item.status,
          getStatusLabel(item.status),
          item.description,
          item.reject_reason,
          item.transaction_reference_code
        ].filter(Boolean).join(' | '));
        return text.includes(query);
      });
  }, [requests, statusFilter, searchText]);

  const stats = useMemo(() => {
    const result = { all: requests.length, pending: 0, completed: 0, rejected: 0, total_completed: 0 };
    requests.forEach((item) => {
      if (result[item.status] !== undefined) result[item.status] += 1;
      if (item.status === 'completed') result.total_completed += Number(item.amount || 0);
    });
    return result;
  }, [requests]);

  const confirmRequest = async (item) => {
    const note = window.prompt(`تأكيد طلب ${item.request_code} وشحن المحل تلقائياً؟\nملاحظة الشركة اختيارية:`, 'تم تأكيد وصول المبلغ من الشركة');
    if (note === null) return;

    try {
      setConfirmingId(item.id);
      setError('');
      setSuccess('');
      const response = await apiFetch(`/masrafi-requests/${item.id}/confirm`, {
        method: 'POST',
        body: JSON.stringify({ company_note: note })
      });
      const data = response ? await response.json().catch(() => ({})) : {};
      if (!response?.ok) {
        setError(data.message || 'فشل تأكيد طلب مصرفي');
        return;
      }
      setSuccess(`${data.message || 'تم تأكيد الطلب'}${data.reference_code ? ` — العملية: ${data.reference_code}` : ''}`);
      await fetchRequests();
    } catch (err) {
      console.error(err);
      setError('حدث خطأ أثناء تأكيد الطلب');
    } finally {
      setConfirmingId('');
    }
  };

  const rejectRequest = async (item) => {
    const reason = window.prompt(`سبب رفض طلب ${item.request_code}:`, '');
    if (reason === null) return;
    if (!reason.trim()) {
      setError('سبب الرفض مطلوب');
      return;
    }

    try {
      setRejectingId(item.id);
      setError('');
      setSuccess('');
      const response = await apiFetch(`/masrafi-requests/${item.id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reject_reason: reason.trim() })
      });
      const data = response ? await response.json().catch(() => ({})) : {};
      if (!response?.ok) {
        setError(data.message || 'فشل رفض طلب مصرفي');
        return;
      }
      setSuccess(data.message || 'تم رفض الطلب');
      await fetchRequests();
    } catch (err) {
      console.error(err);
      setError('حدث خطأ أثناء رفض الطلب');
    } finally {
      setRejectingId('');
    }
  };

  const getReceiptSource = (request) => {
    return request?.receipt_view_url || request?.receipt_url || request?.receipt_data || '';
  };

  if (loading) {
    return (
      <div dir="rtl" className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-3xl shadow p-8 text-xl font-bold text-gray-700">جاري تحميل طلبات مصرفي...</div>
      </div>
    );
  }

  return (
    <div dir="rtl" className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="bg-gradient-to-br from-gray-950 via-orange-900 to-black text-white rounded-3xl p-5 md:p-8 shadow-lg mb-6">
          <button onClick={() => navigate('/')} className="bg-white/10 hover:bg-white/20 text-white font-bold px-4 py-2 rounded-xl mb-5">← رجوع للرئيسية</button>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <div>
              <h1 className="text-3xl md:text-5xl font-bold">🏦 طلبات مصرفي</h1>
              <p className="text-orange-100 mt-3 text-sm md:text-base">
                كل طلب مصرفي يحتاج وصل إجباري، ويبقى معلقاً حتى تأكيد الشركة، ثم يشحن النظام المحل تلقائياً.
              </p>
            </div>
            <button onClick={() => navigate('/transactions')} className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-xl font-bold shadow">➕ إنشاء طلب من العمليات</button>
          </div>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4 mb-4">⚠️ {error}</div>}
        {success && <div className="bg-green-50 border border-green-200 text-green-700 rounded-2xl p-4 mb-4">✅ {success}</div>}

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <SummaryCard title="كل الطلبات" value={stats.all} />
          <SummaryCard title="بانتظار التأكيد" value={stats.pending} color="yellow" />
          <SummaryCard title="مكتملة" value={stats.completed} color="green" />
          <SummaryCard title="مرفوضة" value={stats.rejected} color="red" />
          <SummaryCard title="إجمالي المكتمل" value={formatNumber(stats.total_completed)} color="orange" />
        </div>

        <div className="bg-white rounded-3xl shadow p-5 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder="بحث باسم المحل أو رقم الطلب..." className="md:col-span-2 border-2 border-gray-200 focus:border-orange-500 outline-none rounded-2xl p-4" />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border-2 border-gray-200 focus:border-orange-500 outline-none rounded-2xl p-4 bg-white">
              <option value="all">كل الحالات</option>
              <option value="pending">بانتظار تأكيد الشركة</option>
              <option value="completed">مؤكد ومكتمل</option>
              <option value="rejected">مرفوض</option>
              <option value="cancelled">ملغي</option>
            </select>
          </div>
        </div>

        {filteredRequests.length === 0 ? (
          <div className="bg-white rounded-3xl shadow p-10 text-center text-gray-500">لا توجد طلبات مصرفي مطابقة</div>
        ) : (
          <div className="space-y-4">
            {filteredRequests.map((item) => (
              <div key={item.id} className="bg-white rounded-3xl shadow p-5 border border-gray-100">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="font-bold text-gray-900 text-lg">{item.request_code || String(item.id).slice(0, 8)}</span>
                      <span className={`border rounded-full px-3 py-1 text-sm font-bold ${getStatusClass(item.status)}`}>{getStatusLabel(item.status)}</span>
                      {item.duplicate_of_id && <span className="bg-orange-100 text-orange-800 border border-orange-200 rounded-full px-3 py-1 text-sm font-bold">⚠️ وصل مكرر محتمل</span>}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-gray-700">
                      <Info label="المحل" value={item.store_name || '-'} />
                      <Info label="المبلغ" value={formatNumber(item.amount)} />
                      <Info label="أنشأه" value={item.user_name || '-'} />
                      <Info label="تاريخ الطلب" value={formatDate(item.created_at)} />
                      <Info label="أكد بواسطة" value={item.confirmed_by_name || '-'} />
                      <Info label="العملية الرسمية" value={item.transaction_reference_code || '-'} />
                    </div>

                    {item.description && <p className="mt-3 text-gray-600 bg-gray-50 rounded-2xl p-3">📝 {item.description}</p>}
                    {item.reject_reason && <p className="mt-3 text-red-700 bg-red-50 rounded-2xl p-3">سبب الرفض: {item.reject_reason}</p>}
                    {item.company_note && <p className="mt-3 text-green-700 bg-green-50 rounded-2xl p-3">ملاحظة الشركة: {item.company_note}</p>}
                  </div>

                  <div className="flex flex-col gap-2 min-w-full lg:min-w-[220px]">
                    <button onClick={() => setSelectedReceipt(item)} className="bg-gray-900 hover:bg-black text-white font-bold py-3 px-4 rounded-xl">📎 عرض الوصل</button>
                    {item.status === 'pending' && (
                      <>
                        <button onClick={() => confirmRequest(item)} disabled={confirmingId === item.id || rejectingId === item.id} className="bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white font-bold py-3 px-4 rounded-xl">
                          {confirmingId === item.id ? 'جاري التأكيد...' : '✅ تأكيد الشركة'}
                        </button>
                        <button onClick={() => rejectRequest(item)} disabled={confirmingId === item.id || rejectingId === item.id} className="bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white font-bold py-3 px-4 rounded-xl">
                          {rejectingId === item.id ? 'جاري الرفض...' : '❌ رفض'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedReceipt && (
        <div className="fixed inset-0 bg-black/70 z-[80] p-4 flex items-center justify-center" onClick={() => setSelectedReceipt(null)}>
          <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-auto p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-4 mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">📎 وصل طلب مصرفي</h2>
                <p className="text-gray-500 text-sm mt-1">{selectedReceipt.request_code} — {selectedReceipt.store_name}</p>
              </div>
              <button onClick={() => setSelectedReceipt(null)} className="bg-gray-100 hover:bg-gray-200 rounded-xl px-4 py-2 font-bold">إغلاق</button>
            </div>

            {getReceiptSource(selectedReceipt) ? (
              String(selectedReceipt.receipt_type || '').includes('pdf') ? (
                <iframe title="receipt" src={getReceiptSource(selectedReceipt)} className="w-full h-[70vh] border rounded-2xl" />
              ) : (
                <img src={getReceiptSource(selectedReceipt)} alt="وصل مصرفي" className="w-full rounded-2xl border object-contain" />
              )
            ) : (
              <div className="bg-yellow-50 text-yellow-800 rounded-2xl p-5 font-bold">
                لا يوجد رابط عرض متاح لهذا الوصل حالياً. تأكد من إعدادات Supabase Storage في السيرفر.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ title, value, color = 'blue' }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-800 border-blue-100',
    yellow: 'bg-yellow-50 text-yellow-800 border-yellow-100',
    green: 'bg-green-50 text-green-800 border-green-100',
    red: 'bg-red-50 text-red-800 border-red-100',
    orange: 'bg-orange-50 text-orange-800 border-orange-100'
  };
  return (
    <div className={`rounded-3xl border p-5 ${colors[color] || colors.blue}`}>
      <p className="text-sm opacity-80">{title}</p>
      <p className="text-2xl font-bold mt-2">{value}</p>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="bg-gray-50 rounded-2xl p-3">
      <div className="text-gray-500 text-xs">{label}</div>
      <div className="font-bold text-gray-900 mt-1 break-words">{value}</div>
    </div>
  );
}

export default MasrafiRequests;
